import { useNavigate } from "react-router-dom";
import { Gem, Leaf, ArrowRight, LogOut, HeartPulse, Compass, CalendarCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const Card = ({ testid, icon: Icon, eyebrow, title, subtitle, points, cta, onClick, accent }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group relative text-left rounded-[20px] border border-[color:var(--border-subtle)] bg-[var(--surface-2)] p-8 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]"
  >
    <div className="flex items-center justify-between">
      <span
        className="grid place-items-center h-14 w-14 rounded-[16px] text-[#f7fbf9]"
        style={{ backgroundColor: accent }}
      >
        <Icon className="h-7 w-7" />
      </span>
      <ArrowRight className="h-5 w-5 text-[var(--text-faint)] transition-transform group-hover:translate-x-1" />
    </div>
    <div className="mt-6">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)]">{eyebrow}</div>
      <h2 className="font-serif-display text-2xl text-[var(--text)] mt-1">{title}</h2>
      <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed">{subtitle}</p>
    </div>
    <ul className="mt-5 space-y-1.5">
      {points.map((p) => (
        <li key={p} className="flex items-center gap-2 text-[13px] text-[var(--text)]">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
          {p}
        </li>
      ))}
    </ul>
    <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: accent }}>
      {cta} <ArrowRight className="h-4 w-4" />
    </div>
  </button>
);

export default function CommandSelect() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const first = (user?.display_name || "there").split(" ")[0];

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      <header className="flex items-center justify-between px-6 sm:px-10 h-16">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center h-9 w-9 rounded-[10px] bg-[var(--green)] text-[#f7fbf9]">
            <Gem className="h-5 w-5" />
          </span>
          <span className="font-serif-display text-[15px] text-[var(--text)]">Command Centers</span>
        </div>
        <Button variant="ghost" onClick={logout} data-testid="select-sign-out"
          className="text-[var(--text-muted)] hover:text-[var(--danger)]">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </header>

      <main className="flex-1 flex flex-col justify-center px-6 sm:px-10 py-10 max-w-5xl mx-auto w-full">
        <div className="mb-10">
          <div className="h-[2px] w-16 bg-[var(--gold)] mb-5" />
          <h1 className="font-serif-display text-3xl sm:text-4xl tracking-[-0.02em] text-[var(--text)]">
            Welcome back, {first}.
          </h1>
          <p className="mt-2 text-[var(--text-muted)] max-w-xl">
            Choose the command center for what you're focused on right now. You can switch anytime.
          </p>
        </div>

        <button
          onClick={() => navigate("/central")}
          data-testid="choose-central"
          className="group relative w-full text-left rounded-[20px] border border-[color:rgba(31,115,85,0.35)] bg-[var(--green)] text-[#f4f8f5] p-7 mb-5 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] overflow-hidden"
        >
          <div className="flex items-center gap-5">
            <span className="grid place-items-center h-14 w-14 rounded-[16px] bg-[color:rgba(198,139,60,0.25)] text-[var(--gold)] shrink-0">
              <Compass className="h-7 w-7" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:rgba(244,248,245,0.7)]">See everything together</div>
              <h2 className="font-serif-display text-2xl mt-0.5">Command Central</h2>
              <p className="text-sm text-[color:rgba(244,248,245,0.82)] mt-1.5 leading-relaxed max-w-2xl">
                One overview across Wealth, Life &amp; Wellness — build reports and get an AI read on how you're doing daily, weekly, monthly, quarterly and yearly.
              </p>
            </div>
            <ArrowRight className="hidden sm:block h-6 w-6 text-[color:rgba(244,248,245,0.8)] transition-transform group-hover:translate-x-1 shrink-0" />
          </div>
        </button>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Card
            testid="choose-wealth"
            icon={Gem}
            accent="#1f7355"
            eyebrow="Money & stewardship"
            title="Wealth Command Center"
            subtitle="Track net worth, budget with intention, plan purchases and get AI-guided financial insight."
            points={["Accounts, cash flow & budgets", "Credit optimization & goals", "AI wealth advisor"]}
            cta="Enter Wealth"
            onClick={() => navigate("/wealth")}
          />
          <Card
            testid="choose-life"
            icon={Leaf}
            accent="#c68b3c"
            eyebrow="The Vine & Branch"
            title="Life Command Center"
            subtitle="A faith-aligned life dashboard that turns vision into daily points, measurable progress and grounded guidance."
            points={["Daily command & readiness score", "Life planning, areas & reviews", "Command AI — grounded guidance"]}
            cta="Enter Life"
            onClick={() => navigate("/life")}
          />
          <Card
            testid="choose-wellness"
            icon={HeartPulse}
            accent="#34705f"
            eyebrow="Health & glow"
            title="Wellness Command Center"
            subtitle="Radiant health, sustainable strength and confident presence — with an AI strategist who knows your whole life."
            points={["Daily habits & readiness", "Goals, progress & glow rituals", "AI wellness strategist"]}
            cta="Enter Wellness"
            onClick={() => navigate("/wellness")}
          />
        </div>

        <button
          onClick={() => navigate("/rhythm")}
          data-testid="choose-rhythm"
          className="group relative w-full text-left rounded-[20px] border border-[color:rgba(31,90,73,0.35)] bg-[var(--surface-2)] p-7 mt-5 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]"
        >
          <div className="flex items-center gap-5">
            <span className="grid place-items-center h-14 w-14 rounded-[16px] text-[#f7fbf9] shrink-0" style={{ backgroundColor: "#1f5a49" }}>
              <CalendarCheck className="h-7 w-7" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)]">The weekly rhythm that compounds</div>
              <h2 className="font-serif-display text-2xl text-[var(--text)] mt-0.5">Rhythm Command Center</h2>
              <p className="text-sm text-[var(--text-muted)] mt-1.5 leading-relaxed max-w-2xl">
                One page to review the week and one to plan the next — across faith, health, work, wealth and relationships. Do this every week and success becomes inevitable.
              </p>
            </div>
            <ArrowRight className="hidden sm:block h-6 w-6 text-[var(--text-faint)] transition-transform group-hover:translate-x-1 shrink-0" />
          </div>
        </button>
      </main>
    </div>
  );
}
